import {
    useInventory,
    useReapproNeeds,
    useStoreInventories,
    useUpdateInventoryFinishedStatus,
    useUpdateInventoryStatus,
} from '@gozoki/api';
import {
    Backdrop,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    DashboardActionCard,
    DashboardCard,
    DashboardInfoCard,
} from '../../utils/dashboard/dashboardCards';

import { frenchFormat } from '@gozoki/tools';
import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useInventoryProductRows } from '../../utils/hooks/useInventoryProductRows';
import { fonts } from '../../utils/theme/fonts.theme';
import { InventoryDetails } from './InventoryDetails';
import { InventoryGrid } from './InventoryGrid';

export interface PendingInventoryData {
    inventoryDetail: NonNullable<ReturnType<typeof useInventory>['data']>;
    isPending: boolean;
    handleValidate: () => void;
    handleCancel: () => void;
}

interface PendingInventoryTabProps {
    shopId: number;
    onInventoryDataChange?: (data: PendingInventoryData | null) => void;
    setTab: (tab: number) => void;
}

export const PendingInventoryTab = (props: PendingInventoryTabProps) => {
    const { shopId, onInventoryDataChange, setTab } = props;
    const queryClient = useQueryClient();

    const { data: inventories, isLoading: isLoadingInventories } = useStoreInventories(shopId);

    const [showDeltaWarningDialog, setShowDeltaWarningDialog] = useState<boolean>(false);
    const [problematicProducts, setProblematicProducts] = useState<string[]>([]);
    const [validatedProducts, setValidatedProducts] = useState<Set<string>>(new Set());
    // const [hasShownDeltaWarning, setHasShownDeltaWarning] = useState<boolean>(false);
    const [isWaitingForInventoryFinish, setIsWaitingForInventoryFinish] = useState<boolean>(false);
    const maxDelta = 2; // Maximum acceptable absolute delta

    const { mutate: updateInventoryStatus, isPending: isUpdatingInventory } =
        useUpdateInventoryStatus();
    const { mutate: updateInventoryFinishedStatus, isPending: isFinishingInventory } =
        useUpdateInventoryFinishedStatus();

    // Find the first pending inventory
    const pendingInventory = useMemo(() => {
        if (!inventories) return undefined;
        return inventories.find((inv) => inv.status === 'pending');
    }, [inventories]);

    const {
        data: inventoryDetail,
        isPending: isLoading,
        refetch: refetchInventory,
    } = useInventory(pendingInventory?.id ?? 0);

    const dayBefore = useMemo(() => {
        if (!inventoryDetail?.createdAt) return null;
        return DateTime.fromISO(inventoryDetail.createdAt).minus({ days: 1 }).toISODate();
    }, [inventoryDetail?.createdAt]);

    const { data: reapproNeedsData, error: reapproNeedsError } = useReapproNeeds(
        inventoryDetail?.storeReference,
        dayBefore
    );

    useEffect(() => {
        if (reapproNeedsError) {
            console.error('Error fetching reappro needs:', reapproNeedsError);
        }
    }, [reapproNeedsError]);

    // Reset waiting state when inventory is finished
    useEffect(() => {
        if (isWaitingForInventoryFinish && inventoryDetail?.isFinished) {
            setIsWaitingForInventoryFinish(false);
        }
    }, [isWaitingForInventoryFinish, inventoryDetail?.isFinished]);

    const productRows = useInventoryProductRows(inventoryDetail, reapproNeedsData ?? null);

    // Combined loading state - keeps loading overlay visible until UI updates
    const isFinishingAndWaiting = isFinishingInventory || isWaitingForInventoryFinish;

    const handleValidate = useCallback(
        () =>
            updateInventoryStatus(
                { id: pendingInventory?.id ?? 0, status: 'validated' },
                {
                    onSuccess: () => {
                        // refetchInventory() is redundant if you invalidate
                        queryClient.invalidateQueries({ queryKey: ['storeInventories', shopId] });
                        setTab(3);
                    },
                }
            ),
        [updateInventoryStatus, pendingInventory?.id, queryClient, shopId, setTab]
    );

    const handleFinishInventory = useCallback(() => {
        if (!pendingInventory) return null;

        // Check for products with high delta
        // const productsWithHighDelta = productRows.filter((row) => -row.delta > maxDelta);

        // if (productsWithHighDelta.length > 0) {
        //     const productRefs = productsWithHighDelta.map((p) => p.reference);

        //     // If this is the first time, just show the warning
        //     if (!hasShownDeltaWarning) {
        //         setProblematicProducts(productRefs);
        //         setShowDeltaWarningDialog(true);
        //         setHasShownDeltaWarning(true);
        //         return null;
        //     }

        //     // Check if all problematic products have been validated
        //     const allValidated = productRefs.every((ref) => validatedProducts.has(ref));

        //     if (!allValidated) {
        //         // Show warning that not all products are validated
        //         setShowDeltaWarningDialog(true);
        //         return null;
        //     }
        // }

        // If no high deltas, or checks passed, proceed.
        setIsWaitingForInventoryFinish(true);
        updateInventoryFinishedStatus(
            {
                id: pendingInventory?.id,
                isFinished: true,
            },
            {
                onSuccess: () => {
                    setProblematicProducts([]);
                    // Invalidate the detail query
                    queryClient.invalidateQueries({
                        queryKey: ['inventory', pendingInventory?.id],
                    });
                    // Invalidate the store's inventory list
                    queryClient.invalidateQueries({
                        queryKey: ['storeInventories', shopId],
                    });
                },
                onError: () => {
                    // Reset waiting state on error
                    setIsWaitingForInventoryFinish(false);
                },
            }
        );
        return null;
    }, [pendingInventory, updateInventoryFinishedStatus, queryClient, shopId]);

    const handleCancel = useCallback(
        () =>
            updateInventoryStatus(
                { id: pendingInventory?.id ?? 0, status: 'canceled' },
                {
                    onSuccess: () => {
                        // Invalidate both just to be safe
                        queryClient.invalidateQueries({ queryKey: ['storeInventories', shopId] });
                        queryClient.invalidateQueries({
                            queryKey: ['inventory', pendingInventory?.id],
                        });
                    },
                }
            ),
        [pendingInventory?.id, updateInventoryStatus, queryClient, shopId]
    );

    const handleValidationCheckboxChange = useCallback((productRef: string, checked: boolean) => {
        setValidatedProducts((prev) => {
            const newSet = new Set(prev);
            if (checked) {
                newSet.add(productRef);
            } else {
                newSet.delete(productRef);
            }
            return newSet;
        });
    }, []);

    // Notify parent component when inventory data changes
    useEffect(() => {
        if (inventoryDetail && pendingInventory) {
            onInventoryDataChange?.({
                inventoryDetail,
                isPending: isUpdatingInventory,
                handleValidate,
                handleCancel,
            });
        } else {
            onInventoryDataChange?.(null);
        }
    }, [
        inventoryDetail,
        pendingInventory,
        handleValidate,
        handleCancel,
        onInventoryDataChange,
        isUpdatingInventory,
    ]);

    // If no pending inventory, show message
    if (isLoadingInventories) {
        return (
            <Typography
                sx={{
                    textAlign: 'center',
                    paddingY: '20px',
                }}
            >
                Recherche d'un inventaire en cours
            </Typography>
        );
    }

    if (!pendingInventory) {
        return (
            <Typography
                sx={{
                    textAlign: 'center',
                    paddingY: '20px',
                }}
                variant="h6"
            >
                Aucun inventaire en cours
            </Typography>
        );
    }

    if (!inventoryDetail) {
        return (
            <Typography
                sx={{
                    textAlign: 'center',
                    paddingY: '20px',
                }}
            >
                Inventaire trouvé, chargement
            </Typography>
        );
    }

    if (isLoading) {
        return (
            <Typography
                sx={{
                    textAlign: 'center',
                    paddingY: '20px',
                }}
            >
                Chargement...
            </Typography>
        );
    }

    return (
        <div style={{ flex: 1, position: 'relative' }}>
            {inventoryDetail.isFinished ? (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        marginBottom: 20,
                        ...fonts.roboto,
                    }}
                >
                    <DashboardInfoCard
                        label="Inventaire terminé"
                        info="Tous les articles en boutique ont été scannés"
                    />
                    <DashboardCard
                        label="Articles scannés (inventaire en cours)"
                        bigValue={`${frenchFormat(inventoryDetail.incomingStock.length)}`}
                        orange
                    />
                </div>
            ) : (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        marginBottom: 20,
                        ...fonts.roboto,
                    }}
                >
                    <DashboardInfoCard
                        label="Inventaire en cours"
                        info="Scanner tous les articles en boutique puis marquer l'inventaire comme terminé"
                    />
                    <DashboardCard
                        label="Articles scannés (inventaire en cours)"
                        bigValue={`${frenchFormat(inventoryDetail.incomingStock.length)}`}
                        orange
                    />
                    <DashboardActionCard
                        label="Marquer l'inventaire comme terminé"
                        subLabel="Une fois tous les articles scannés, marquer l'inventaire comme terminé"
                        actionLabel="L'inventaire est terminé"
                        action={handleFinishInventory}
                        disabled={isFinishingAndWaiting}
                    />
                </div>
            )}
            <InventoryDetails
                label={inventoryDetail.label || ''}
                comment={inventoryDetail.comment || ''}
                inventoryId={inventoryDetail.id}
                onValidate={() => refetchInventory()}
            />
            <InventoryGrid
                isLoading={isLoading}
                productRows={productRows}
                inventoryStatus={inventoryDetail.status}
                problematicProducts={problematicProducts}
                showValidationColumn={false}
                validatedProducts={validatedProducts}
                onValidationChange={handleValidationCheckboxChange}
            />

            {/* Warning dialog for high delta products */}
            <Dialog
                open={showDeltaWarningDialog}
                onClose={() => setShowDeltaWarningDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {problematicProducts.length === 1
                        ? 'Écart important détecté'
                        : 'Écarts importants détectés'}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" paragraph>
                        {problematicProducts.length === 1
                            ? 'Un produit présente'
                            : `${problematicProducts.length} produits présentent`}{' '}
                        un déficit supérieur à {maxDelta} unités entre le stock théorique (après
                        livraison) et le stock relevé.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {problematicProducts.length === 1 ? (
                            <Typography variant="body2" color="text.secondary">
                                Ce produit est affiché en rouge dans le tableau. Veuillez cocher la
                                case "Valider" pour ce produit après l'avoir vérifié manuellement et
                                rescanné si besoin.
                            </Typography>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                Ces produits sont affichés en rouge dans le tableau. Veuillez cocher
                                la case "Valider" pour chaque produit après les avoir vérifiés
                                manuellement et rescannés si besoin.
                            </Typography>
                        )}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowDeltaWarningDialog(false)} color="primary">
                        Compris
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Loading overlay when finishing inventory */}
            <Backdrop
                open={isFinishingAndWaiting}
                sx={{
                    color: '#fff',
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    position: 'absolute',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                }}
            >
                <div style={{ textAlign: 'center' }}>
                    <CircularProgress color="inherit" size={60} />
                </div>
            </Backdrop>
        </div>
    );
};
